import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ViewModeItem } from "../models/ViewModeItem";
import { ViewModeItems } from "../models/ViewModeItem";
import { SortItem, AvailableSortItems } from "../models/SortItem";
import { LookupItem } from "../models/LookupItem";
import { FacetResult } from "../models/FacetResult";
import { EventItem } from "../models/EventItem";
import { SuggestItem } from "../models/SuggestItem";
import { SuggestFilter } from '../models/common/SuggestFilter';

export interface CorporateEventsState {
  viewMode: ViewModeItem;
  orderBy: SortItem;
  keywords: string;
  suggestions: SuggestItem[];
  selectedCategory: undefined | LookupItem;
  selectedLocations: undefined | LookupItem[];
  isLoading: boolean;
  isSuggestLoading: boolean;
  canLoadMore: boolean;
  items: EventItem[];
  pageSize: number;
  page: number;
  initDone: boolean;
}

const initialState: CorporateEventsState = {
  viewMode: ViewModeItems[0],
  orderBy: AvailableSortItems[1],
  keywords: "",
  suggestions: [],
  selectedCategory: undefined,
  selectedLocations: undefined,
  isLoading: false,
  isSuggestLoading: false,
  canLoadMore: false,
  items: [],
  pageSize: 9,
  page: 1,
  initDone: false,
};

export type SearchFilter = {
  keywords?: string;
  category?: string;
  locations?: string[];
  sortBy?: string;
  sortAsc?: boolean;
  path?: string;
  page: number;
  pageSize: number;
  includedIds?: string[],
  excludedIds?: string[],
};

export const searchCorporateEventsSuggest = createAsyncThunk<SuggestItem[], SuggestFilter>(
  "data/searchCorporateEventsSuggest",
  async (payload: SuggestFilter): Promise<SuggestItem[]> => {
    const response = await fetch("/eventssearch/suggest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as SuggestItem[];
  }
);

export const searchCorporateEvents = createAsyncThunk<
  FacetResult<EventItem>,
  SearchFilter
>(
  "data/searchCorporateEvents",
  async (payload: SearchFilter): Promise<FacetResult<EventItem>> => {
    const response = await fetch("/eventssearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as FacetResult<EventItem>;
  }
);

export const corporateEventsSlice = createSlice({
  name: "corporateEvents",
  initialState,
  reducers: {
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    changeViewMode: (state, action: PayloadAction<ViewModeItem>) => {
      state.viewMode = action.payload;
    },
    changeKeywords: (state, action: PayloadAction<string>) => {
      if (state.keywords!== action.payload)
      {
        state.items = [];
        state.canLoadMore = false;
      }
      state.keywords = action.payload;
      state.page = 1;
    },
    changeCategory: (state, action: PayloadAction<undefined | LookupItem>) => {
      if (state.selectedCategory?.value !== action.payload?.value) {
        state.items = [];
        state.canLoadMore = false;
      }
      state.selectedCategory = action.payload;
      state.page = 1;
    },
    changeLocations: (
      state,
      action: PayloadAction<undefined | LookupItem[]>
    ) => {
      state.items = [];
      state.selectedLocations = action.payload;
      state.page = 1;
      state.canLoadMore = false;
    },
    changeInit: (state, action: PayloadAction<boolean>) => {
      state.initDone = action.payload;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchCorporateEvents.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        searchCorporateEvents.fulfilled,
        (state, action: PayloadAction<FacetResult<EventItem>>) => {
          state.isLoading = false;

          if (action.payload) {
            if (state.page == 1) {
              state.items = [];
            }
            if (action.payload.hits.length > 0) {
              state.items.push(...action.payload.hits);
            }

            state.canLoadMore =
              action.payload.totalHits > state.items.length &&
              state.items.length > 0;
          }
        }
      )
      .addCase(searchCorporateEvents.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(searchCorporateEventsSuggest.pending, (state) => {
        state.isSuggestLoading = true;
      })
      .addCase(
        searchCorporateEventsSuggest.fulfilled,
        (state, action: PayloadAction<SuggestItem[]>) => {
          state.isSuggestLoading = false;

          state.suggestions = [];

          if (action.payload.length > 0) {
            state.suggestions = [...action.payload];
          }
        }
      )
      .addCase(searchCorporateEventsSuggest.rejected, (state) => {
        state.isSuggestLoading = false;
      });
  },
});

export const {
  changePage,
  changeViewMode,
  changeKeywords,
  changeCategory,
  changeLocations,
  changeInit,
  clearSuggestions
} = corporateEventsSlice.actions;

export default corporateEventsSlice.reducer;
